import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Grid3x3, Search, Heart, ShoppingCart, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";

export default function MobileBottomNav() {
  const loc = useLocation();
  const nav = useNavigate();
  const { itemCount, wishlist } = useCart();
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [suggests, setSuggests] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!searchOpen) return;
    const t = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(t);
  }, [searchOpen]);

  useEffect(() => {
    if (!q.trim()) { setSuggests([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products/search-suggest", { params: { q } });
        setSuggests(data.items || []);
      } catch {}
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const closeSearch = () => { setSearchOpen(false); setQ(""); setSuggests([]); };
  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    closeSearch();
    nav(`/shop?q=${encodeURIComponent(q.trim())}`);
  };

  const items = [
    { to: "/", icon: Home, label: "Home", tid: "mnav-home" },
    { to: "/shop", icon: Grid3x3, label: "Shop", tid: "mnav-shop" },
    { action: () => setSearchOpen(true), icon: Search, label: "Search", tid: "mnav-search" },
    { to: "/wishlist", icon: Heart, label: "Wishlist", tid: "mnav-wishlist", badge: wishlist.length },
    { to: "/cart", icon: ShoppingCart, label: "Cart", tid: "mnav-cart", badge: itemCount },
  ];

  return (
    <>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-ivory border-t hairline" data-testid="mobile-bottom-nav">
        <div className="grid grid-cols-5">
          {items.map((it) => {
            const Icon = it.icon;
            const active = it.to && loc.pathname === it.to;
            const inner = (
              <>
                <Icon className={`w-5 h-5 ${active ? "gold-text" : ""}`} strokeWidth={1.5} />
                <span className={`text-[10px] ${active ? "gold-text" : "text-muted-foreground"}`}>{it.label}</span>
                {it.badge > 0 && <span className="absolute top-1 right-1/4 bg-[#B8860B] text-ivory text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{it.badge}</span>}
              </>
            );
            return it.to ? (
              <Link key={it.label} to={it.to} className="relative flex flex-col items-center justify-center py-2 gap-1" data-testid={it.tid}>{inner}</Link>
            ) : (
              <button key={it.label} onClick={it.action} className="relative flex flex-col items-center justify-center py-2 gap-1" data-testid={it.tid}>{inner}</button>
            );
          })}
        </div>
      </nav>

      {searchOpen && (
        <div className="md:hidden fixed inset-0 z-[100] bg-ivory flex flex-col" data-testid="mobile-search-overlay">
          <form onSubmit={submitSearch} className="flex items-center gap-2 p-4 border-b hairline bg-white">
            <Search className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.5} />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search brass diya, ganesha, pooja items…"
              className="flex-1 bg-transparent outline-none text-base"
              data-testid="mobile-overlay-search-input"
            />
            <button type="button" onClick={closeSearch} aria-label="Close search" className="p-2.5 -m-1" data-testid="mobile-overlay-search-close">
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </form>
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {suggests.map((s) => (
              <Link key={s.slug} to={`/product/${s.slug}`} onClick={closeSearch} className="flex items-center gap-3 p-2 border hairline rounded-lg bg-white" data-testid={`msearch-${s.slug}`}>
                <img src={s.images?.[0]} className="w-12 h-12 object-cover rounded" alt="" />
                <div className="flex-1 text-sm">{s.name}</div>
                <div className="text-sm gold-text whitespace-nowrap">₹{s.price}</div>
              </Link>
            ))}
            {q.trim() && (
              <button type="button" onClick={submitSearch} className="w-full mt-2 btn-gold rounded-full py-3 text-xs uppercase tracking-widest" data-testid="msearch-view-all">
                View all results for “{q.trim()}”
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
